import React from 'react';
import { Button, Input, Card, CardBody, Textarea } from '@heroui/react';
import { Plus, Trash, ArrowUp, ArrowDown } from '@phosphor-icons/react';
import { AssetType, AssetScope } from 'shared/types/asset';

interface AssetTypeBuilderProps {
  types: AssetType[];
  onChange: (types: AssetType[]) => void;
  className?: string;
}

export const AssetTypeBuilder: React.FC<AssetTypeBuilderProps> = ({
  types,
  onChange,
  className = '',
}) => {
  const handleAddType = () => {
    const newType: AssetType = {
      id: Date.now(), // Temporary ID for new types
      assetCategoryId: 0,
      name: 'Nieuw Type',
      description: '',
      scopes: [AssetScope.UNIT], // Default to UNIT level
      displayOrder: types.length,
      active: true,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    onChange([...types, newType]);
  };

  const scopeOptions = [
    {
      value: AssetScope.BUILDING,
      label: 'Gebouw',
      description: 'Voorziening behoort tot een heel gebouw',
    },
    {
      value: AssetScope.UNIT,
      label: 'Unit',
      description: 'Voorziening behoort tot een specifieke unit',
    },
    {
      value: AssetScope.ROOM,
      label: 'Kamer',
      description: 'Voorziening behoort tot een specifieke kamer',
    },
    {
      value: AssetScope.PORTABLE,
      label: 'Draagbaar',
      description: 'Voorziening kan verplaatst worden tussen locaties',
    },
  ];

  const handleUpdateType = (index: number, updates: Partial<AssetType>) => {
    const newTypes = [...types];
    newTypes[index] = { ...newTypes[index], ...updates };
    onChange(newTypes);
  };

  const handleRemoveType = (index: number) => {
    const type = types[index];
    const typeName = type.name || 'dit type';

    if (!confirm(`Weet je zeker dat je "${typeName}" wilt verwijderen?`)) {
      return;
    }

    const newTypes = types.filter((_, i) => i !== index);
    // Reorder remaining types
    newTypes.forEach((type, i) => {
      type.displayOrder = i;
    });
    onChange(newTypes);
  };

  const handleMoveType = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === types.length - 1)
    ) {
      return;
    }

    const newTypes = [...types];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    // Swap types
    [newTypes[index], newTypes[targetIndex]] = [newTypes[targetIndex], newTypes[index]];

    // Update display order
    newTypes.forEach((type, i) => {
      type.displayOrder = i;
    });

    onChange(newTypes);
  };

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Voorziening Types</h3>
        <Button
          color="primary"
          size="sm"
          onPress={handleAddType}
          startContent={<Plus size={16} />}
        >
          Type Toevoegen
        </Button>
      </div>

      {types.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg">
          <p className="text-gray-500 text-sm">Nog geen types gedefinieerd</p>
          <p className="text-gray-400 text-xs mt-1">
            Klik &quot;Type Toevoegen&quot; om specifieke types te maken (bijv. Oven, Koelkast, Toilet, Lift)
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {types
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map((type, index) => (
              <Card key={type.id} className="mb-2">
                <CardBody className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="flex flex-col gap-1">
                      <Button
                        isIconOnly
                        size="sm"
                        variant="light"
                        onPress={() => handleMoveType(index, 'up')}
                        isDisabled={index === 0}
                      >
                        <ArrowUp size={16} />
                      </Button>
                      <Button
                        isIconOnly
                        size="sm"
                        variant="light"
                        onPress={() => handleMoveType(index, 'down')}
                        isDisabled={index === types.length - 1}
                      >
                        <ArrowDown size={16} />
                      </Button>
                    </div>

                    <div className="flex-1 space-y-2">
                      <Input
                        label="Type Naam"
                        placeholder="bijv. Oven, Koelkast, Toilet, Personenlift"
                        value={type.name}
                        onChange={(e) => handleUpdateType(index, { name: e.target.value })}
                        size="sm"
                        isRequired
                      />

                      <div className="space-y-1">
                        <label className="block text-xs font-medium text-gray-700">
                          Bereik <span className="text-red-500">*</span>
                        </label>
                        <p className="text-xs text-gray-500 mb-1">
                          Waar kan deze voorziening zich bevinden?
                        </p>
                        <div className="flex flex-wrap gap-3">
                          {scopeOptions.map((option) => (
                            <label key={option.value} className="flex items-center gap-1.5 text-sm cursor-pointer">
                              <input
                                type="checkbox"
                                checked={type.scopes?.includes(option.value) ?? false}
                                onChange={(e) => {
                                  const current = type.scopes || [];
                                  const updated = e.target.checked
                                    ? [...current, option.value]
                                    : current.filter((s) => s !== option.value);
                                  handleUpdateType(index, { scopes: updated });
                                }}
                                className="rounded"
                              />
                              {option.label}
                            </label>
                          ))}
                        </div>
                      </div>

                      <Textarea
                        label="Beschrijving (optioneel)"
                        placeholder="Beschrijf dit type voorziening..."
                        value={type.description || ''}
                        onChange={(e) => handleUpdateType(index, { description: e.target.value })}
                        size="sm"
                        rows={2}
                      />
                    </div>

                    <Button
                      isIconOnly
                      size="sm"
                      color="danger"
                      variant="light"
                      onPress={() => handleRemoveType(index)}
                    >
                      <Trash size={18} />
                    </Button>
                  </div>
                </CardBody>
              </Card>
            ))}
        </div>
      )}
    </div>
  );
};
